import type { LayoutDocument } from "../layout/types.js";
import { indexDocument } from "./graph.js";
import { compileBehavior } from "./compileBehavior.js";
import type {
  ListFilterTransitionBucket,
  SceneCatalogDependencyIndex,
} from "./types.js";

function isRowScopedListFilterHandler(
  ownerNodeId: string,
  targetListNodeId: string,
  parentById: Map<string, string | null>,
): boolean {
  if (ownerNodeId === targetListNodeId) {
    return false;
  }

  let currentParent = parentById.get(ownerNodeId);
  while (currentParent) {
    if (currentParent === targetListNodeId) {
      return true;
    }
    currentParent = parentById.get(currentParent);
  }

  return false;
}

/**
 * Precompiles list-filter transition subscriptions by target layout list id.
 * Subscriptions are sourced from `onListFilterExit` / `onListFilterEnter` events.
 */
export function compileListFilterTransitionBuckets(
  document: LayoutDocument,
  catalogDeps: SceneCatalogDependencyIndex,
): {
  layoutNodeById: Map<string, import("../layout/types.js").LayoutNode>;
  bucketsByListId: Map<string, ListFilterTransitionBucket>;
} {
  const indexed = indexDocument(document);
  const bucketsByListId = new Map<string, ListFilterTransitionBucket>();

  for (const [ownerNodeId, graph] of indexed.graphsById.entries()) {
    const compiled = compileBehavior(
      graph,
      ownerNodeId,
      catalogDeps,
      undefined,
      indexed.nodesById,
    );

    for (const handler of compiled.eventHandlers) {
      if (
        handler.eventKind !== "onListFilterExit" &&
        handler.eventKind !== "onListFilterEnter"
      ) {
        continue;
      }
      if (!handler.targetListNodeId) {
        continue;
      }
      const existing = bucketsByListId.get(handler.targetListNodeId) ?? {
        exitHandlers: [],
        enterHandlers: [],
      };
      const transitionHandler = {
        ownerNodeId,
        handler,
        rowScoped: isRowScopedListFilterHandler(
          ownerNodeId,
          handler.targetListNodeId,
          indexed.parentById,
        ),
      };
      if (handler.eventKind === "onListFilterExit") {
        existing.exitHandlers.push(transitionHandler);
      } else {
        existing.enterHandlers.push(transitionHandler);
      }
      bucketsByListId.set(handler.targetListNodeId, existing);
    }
  }

  return {
    layoutNodeById: indexed.nodesById,
    bucketsByListId,
  };
}
